
import {
    computed,
    defineComponent,
    onBeforeMount,
    PropType,
    reactive,
    ref,
    onBeforeUnmount,
} from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { requiredIf, required, integer, minLength, email } from '@vuelidate/validators'
import { binValidator, incorrectSymbolValidator } from '@/common/validators/common.validators'

// types
import { CarrierT, CarrierUpdatePayloadT } from '@/core/types/carriers.types'
import { CityT } from '@/core/types/cities.types'
import { UserT } from '@/core/types/users.types'
import { SecurityGetCheckupStatusResponseT } from '@/core/types/security.types'
import {
    CompanyDataResponseT,
    InProgressDataResponseT,
    InvalidDataResponseT,
    SecurityGetCompanyInformationResponseT,
} from '@/core/types/security.types'

// constants
import { CarriersTypes } from '@/core/constants/carriers.constants'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { CountryT, TransportType } from '@/core/types/companies.types'

// realisation
import { companies } from '@/core/realisations/companies.realisation'
import { cities } from '@/core/realisations/cities.realisation'
import { users } from '@/core/realisations/users.realisation'
import { security } from '@/core/realisations/security.realisation'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useMaska } from '@/common/composable/useMaska'

// constants
import { CARRIER_TYPES } from '@/core/constants/couriers.constants'
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

const COMPANY_CHECKUP_TYPE = 1

const TIMER_INTERVAL = 7 * 1000 // 7s

export default defineComponent({
    components: {
        PageTitle,
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: true,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<CarrierT>,
            default: null,
        },
    },
    emits: ['cancel', 'create', 'update'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { unmaskNumbers } = useMaska()

        const step = ref(1)

        const state = reactive({
            id: undefined as number | undefined,
            name: null as string | null,
            shortName: null as string | null,
            type: null as number | null,
            contactPhone: null as string | null,
            contactName: null as string | null,
            contactEmail: null as string | null,
            logistId: null as number | null,
            jurAddress: null as string | null,
            factAddress: null as string | null,
            bank: null as string | null,
            kbe: null as number | null,
            bankAccount: null as string | null,
            bin: null as string | null,
            bik: null as string | null,
            contractNumber: null as string | null,
            contractDate: null as string | null,
            countryId: 1 as number | null,
            cities: [] as number[],
            activityIds: [] as number[],
            isStandardContract: false,
            isNds: false,
            is_500_error: false,
            vehicleOwner: null,
            transportTypeIds: [] as number[],
            typeTons: 0,
            code1C: null as number | null,
            economicActivityClassifier: null as string | null,
        })

        const CarrierCreateOrUpdateValidation = {
            name: { required, incorrectSymbolValidator },
            shortName: { required, incorrectSymbolValidator },
            type: { required: requiredIf(() => props.isCreate) },
            bank: { required, incorrectSymbolValidator },
            kbe: { required, integer },
            bin: { required, integer, binValidator },
            bik: { required, incorrectSymbolValidator },
            cities: { required, minLength: minLength(1) },
            activityIds: { required, minLength: minLength(1) },
            logistId: { required },
            //
            bankAccount: { required, incorrectSymbolValidator },
            contactName: { required, incorrectSymbolValidator },
            contactPhone: { required },
            jurAddress: { required },
            factAddress: { required },
            countryId: { required },
            contactEmail: { required: requiredIf(() => props.isCreate), email },
            economicActivityClassifier: {
                required: requiredIf(() => state.activityIds.includes(2)),
            },
        }

        const state_form$ = useVuelidate(CarrierCreateOrUpdateValidation, state)

        const sb_state = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
        })

        const resetSbState = () => {
            sb_state.is_checked = false
            sb_state.is_rejected = null
            sb_state.reject_comment = null
            sb_state.is_request_completed = false

            state.is_500_error = false
        }

        const company_data = reactive({
            is_loading: false,
            is_information_loading: false,
            is_not_found: false,
            is_in_progress: false,
            not_found_message: null as string | null,
            update_timer: null as number | null,
            is_loaded: false,
        })

        const createCompanyUpdateTimer = () => {
            // @ts-ignore
            company_data.update_timer = setInterval(getCompanyInformation, TIMER_INTERVAL)
        }

        const removeCompanyUpdateTimer = () => {
            // @ts-ignore
            clearInterval(company_data.update_timer)
            company_data.update_timer = null
        }

        // const isCompanyNotFound = (response: SecurityGetCompanyInformationResponseT): boolean => {
        //     const invalid_response = response as InvalidDataResponseT

        //     const key = invalid_response.key
        //     const message = invalid_response.message

        //     if (key && key === 'not_found') {
        //         company_data.not_found_message = message
        //         return true
        //     }

        //     company_data.not_found_message = null
        //     return false
        // }

        // const isCompanyInProgress = (response: SecurityGetCompanyInformationResponseT): boolean => {
        //     const key = (response as InProgressDataResponseT).key

        //     if (key && key === 'in_progress') return true
        //     return false
        // }

        const getCompanyInformation = async () => {
            company_data.is_loaded = false

            try {
                company_data.is_information_loading = true

                // const company_bin = state.bin as string
                // const response = await security.getCompanyInformation(company_bin)

                // company_data.is_not_found = isCompanyNotFound(response)
                // company_data.is_in_progress = isCompanyInProgress(response)

                if (company_data.is_not_found) {
                    removeCompanyUpdateTimer()
                    return
                }

                if (company_data.is_in_progress) {
                    if (!company_data.update_timer) {
                        createCompanyUpdateTimer()
                    }
                    return
                }

                removeCompanyUpdateTimer()

                loadDataState()

                step.value = 2
                company_data.is_loaded = true
            } catch (error: any) {
                if (error.status === 500) {
                    state.is_500_error = true
                    step.value = 2
                    loadDataState()
                    company_data.is_loaded = true
                }

                notifications.notifyError({
                    error,
                    title: 'При получении информации о компании произошла ошибка',
                })
            } finally {
                company_data.is_information_loading = false
            }
        }

        // const createCompanyInformationRequest = async () => {
        //     try {
        //         company_data.is_loading = true

        //         const company_bin = state.bin as string
        //         await security.createCompanyInformationRequest(company_bin)

        //         getCompanyInformation()
        //     } catch (error) {
        //         notifications.notifyError({
        //             error,
        //             title: 'При создании запроса на получение информации о компании произошла ошибка',
        //         })
        //     } finally {
        //         company_data.is_loading = false
        //     }
        // }

        const checkSbStatus = async () => {
            if (
                sb_state.is_loading ||
                company_data.is_loading ||
                company_data.is_information_loading
            )
                return
            resetSbState()

            try {
                sb_state.is_loading = true

                const response: SecurityGetCheckupStatusResponseT = await security.getCheckupStatus(
                    {
                        identifier: state.bin as string,
                        type: COMPANY_CHECKUP_TYPE,
                    }
                )

                if (!response.success) {
                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                }
                sb_state.is_checked = true

                if (response.data.isAccepted === false) {
                    sb_state.is_rejected = true
                    sb_state.reject_comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    getCompanyInformation()
                    // createCompanyInformationRequest()
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state.is_checked = false
            } finally {
                sb_state.is_loading = false
                sb_state.is_request_completed = true
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitManager = () => {
            if (step.value === 1) {
                checkSbStatus()
                return
            }

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                const payload = { ...state }
                payload.contactPhone = unmaskNumbers(state.contactPhone as string)
                emit('create', payload)
            } else if (props.isUpdate) {
                let activity_ids = [...state.activityIds]

                let carrier_payload_tmp: any = { ...state }

                delete carrier_payload_tmp.activityIds
                carrier_payload_tmp['activity'] = []

                let carrier_payload: CarrierUpdatePayloadT = { ...carrier_payload_tmp }

                carrier_payload.activity = activity_ids.map((activity_id: number) => {
                    let activity_found = props.initialValue.activity.find(
                        (a) => a.activityId === activity_id
                    )

                    if (activity_found) {
                        return { id: activity_id, companyActivityId: activity_found.id }
                    } else {
                        return { id: activity_id }
                    }
                })

                carrier_payload.contactPhone = unmaskNumbers(carrier_payload.contactPhone as string)
                emit('update', { carrier_id: state.id, carrier_payload })
            }
        }

        const data_state = reactive({
            countries: new DataState<CountryT>({}),
            cities: new DataState<CityT>({}),
            logists: new DataState<UserT>({}),
            transport_types: new DataState<TransportType>({}),
        })

        // logists

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name)
                const dispatchers = await users.getDispatchers(page, name)

                data_state.logists.list = [...logists.data, ...dispatchers.data]

                data_state.logists.pagination.total_pages =
                    logists.meta.last_page > dispatchers.meta.last_page
                        ? logists.meta.last_page
                        : dispatchers.meta.last_page

                data_state.logists.is_failed = false
                data_state.logists.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.logists.failure.catchError(error)

                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_failed = true
                }
            } finally {
                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_loading = false
                }
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page)
                data_state.logists.list = data_state.logists.list.concat(logists.data)

                const dispatchers = await users.getDispatchers(page)
                data_state.logists.list = data_state.logists.list.concat(dispatchers.data)

                data_state.logists.pagination.total_pages =
                    logists.meta.last_page > dispatchers.meta.last_page
                        ? logists.meta.last_page
                        : dispatchers.meta.last_page

                data_state.logists.is_failed = false
                data_state.logists.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.logists.failure.catchError(error)

                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_failed = true
                }
            } finally {
                if (!data_state.logists.failure.error_state.is_cancel) {
                    data_state.logists.is_loading = false
                }
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        const loadTransportTypes = async () => {
            data_state.transport_types.pagination.current_page = 1

            try {
                data_state.transport_types.is_loading = true

                const data = await companies.getTransportTypes()
                data_state.transport_types.list = data.data

                data_state.transport_types.is_failed = false
                data_state.transport_types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.transport_types.failure.catchError(error)

                if (!data_state.transport_types.failure.error_state.is_cancel) {
                    data_state.transport_types.is_failed = true
                }
            } finally {
                if (!data_state.transport_types.failure.error_state.is_cancel) {
                    data_state.transport_types.is_loading = false
                }
            }
        }

        // countries

        const loadCountries = async (page: number, search?: string) => {
            data_state.countries.pagination.current_page = page

            try {
                data_state.countries.is_loading = true

                const response = await companies.getCountries(page, search)

                data_state.countries.list = response.data
                data_state.countries.pagination.total_pages = response.meta.last_page

                data_state.countries.is_failed = false
                data_state.countries.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.countries.failure.catchError(error)

                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_failed = true
                }
            } finally {
                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_loading = false
                }
            }
        }

        const loadMoreCountries = async (page: number) => {
            if (page > data_state.countries.pagination.total_pages) return
            data_state.countries.pagination.current_page = page

            try {
                data_state.countries.is_loading = true

                const response = await companies.getCountries(page)
                data_state.countries.list = data_state.countries.list.concat(response.data)

                data_state.countries.pagination.total_pages = response.meta.last_page

                data_state.countries.is_failed = false
                data_state.countries.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.countries.failure.catchError(error)

                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_failed = true
                }
            } finally {
                if (!data_state.countries.failure.error_state.is_cancel) {
                    data_state.countries.is_loading = false
                }
            }
        }

        const searchCountries = async (search_query: string) => {
            // data_state.countries.pagination.current_page
            loadCountries(1, search_query)
        }

        const unshiftFindedCountry = async (country: CountryT) => {
            await loadCountries(data_state.countries.pagination.current_page)
            data_state.countries.list.unshift(country)
        }

        // cities

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        const loadDataState = () => {
            loadLogists(1)
            loadCountries(1)
            loadCities(1)
            loadTransportTypes()
        }

        const fillInfo = () => {
            state.id = props.initialValue.id
            state.logistId = props.initialValue.logistId
            state.name = props.initialValue.name
            state.shortName = props.initialValue.short_name
            state.type = props.initialValue.type_id
            state.contactPhone = props.initialValue.contact_phone
            state.code1C = props.initialValue.code_1c
            state.contactName = props.initialValue.contact_name
            state.jurAddress = props.initialValue.jur_address
            state.factAddress = props.initialValue.fact_address
            state.bank = props.initialValue.bank
            state.bankAccount = props.initialValue.bank_account
            state.bin = props.initialValue.bin
            state.bik = props.initialValue.bik
            state.countryId = props.initialValue.country_id
            state.cities = props.initialValue.cities.map((city) => city.id)
            state.activityIds = props.initialValue.activity.map((a) => a.activityId)
            if (props.initialValue.companyTransports) {
                state.transportTypeIds = props.initialValue.companyTransports.map(
                    (item) => item.transport.id
                )
            }
            state.contractNumber = props.initialValue.contract_number
            state.contractDate = props.initialValue.contract_date
            state.kbe = props.initialValue.kbe
            state.contactEmail = props.initialValue.contact_email
            state.isStandardContract = props.initialValue.is_standard_contract
            state.isNds = props.initialValue.is_nds

            state.economicActivityClassifier = props.initialValue.economy_classifier
        }

        // reading initial value on update
        onBeforeMount(() => {
            if (!props.initialValue) return

            step.value = 2
            loadDataState()
            fillInfo()
        })

        const isNotFound = computed(
            () =>
                step.value === 1 &&
                sb_state.is_rejected === null &&
                sb_state.is_request_completed &&
                !sb_state.is_loading &&
                !company_data.is_information_loading &&
                !state.is_500_error
        )
        const isRejected = computed(
            () => step.value === 1 && sb_state.is_rejected && sb_state.is_request_completed
        )
        const isNotChecked = computed(
            () => step.value === 1 && !sb_state.is_checked && sb_state.is_request_completed
        )

        onBeforeUnmount(removeCompanyUpdateTimer)

        return {
            company_data,
            step,
            resetSbState,
            sb_state,
            CarriersTypes,
            state,
            state_form$,
            data_state,
            emitCancel,
            emitManager,
            loadTransportTypes,
            //
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
            //
            loadCountries,
            loadMoreCountries,
            searchCountries,
            unshiftFindedCountry,
            //
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            //
            isRejected,
            isNotChecked,
            isNotFound,
            //
            CARRIER_TYPES,
            PHONE_NUMBER_MASK,
        }
    },
})
